import { apiClient } from '@/lib/api/client'
import { env } from '@/config/env'
import type { User, UserRole } from '@/types/entities'

export interface UserPage {
  data: User[]
  total: number
  limit: number
  offset: number
}

export interface PasswordChangeResult {
  ok: boolean
  supabase: 'synced' | 'not-linked' | 'unavailable' | 'failed'
}

export const userApi = {
  getUser: async (userId: number): Promise<User> => {
    const response = await apiClient.get(`/api/users/${userId}`)
    return response.data
  },

  /**
   * One page of the user directory.
   *
   * This used to be `getAllUsers()` — every active user in a single array, re-fetched on each
   * visit so the page could filter 1,300+ rows in the browser as you typed. Search and paging are
   * query parameters now, and the server answers them.
   */
  listUsers: async (params: {
    search?: string
    role?: UserRole
    limit?: number
    offset?: number
  } = {}): Promise<UserPage> => {
    const response = await apiClient.get<UserPage>('/api/users', {
      params: {
        ...(params.search ? { search: params.search } : {}),
        ...(params.role ? { role: params.role } : {}),
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    })
    return response.data
  },

  updateUser: async (userId: number, data: Partial<User>): Promise<User> => {
    const response = await apiClient.patch(`/api/users/${userId}`, data)
    return response.data
  },

  /**
   * Change the signed-in account's own password.
   *
   * Credentials travel in the JSON body. This used to PATCH /api/users/:id with the current
   * password in a plaintext `cpass` header, which browsers refuse for non-Latin-1 characters and
   * Firebase Hosting can strip on the way to Cloud Run. `supabase` reports whether the server also
   * updated the artist's Supabase password.
   */
  updatePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<PasswordChangeResult> => {
    const response = await apiClient.post<PasswordChangeResult>('/api/users/me/password', {
      currentPassword,
      newPassword,
    })
    return response.data
  },

  uploadProfileImage: async (userId: number, file: File): Promise<User> => {
    const formData = new FormData()
    formData.append('profile', file)

    const response = await apiClient.post(`/api/users/${userId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  getUserImageUrl: (userId: number, signature: string): string => {
    if (!signature) return ''
    return `${env.apiUrl}/artwork-ms/api/users/${userId}/image#${signature}`
  },

  getShopifyStoreUrl: (shopifyHandle?: string): string => {
    if (!shopifyHandle) return ''
    return `https://myarthaus.com/collections/${shopifyHandle}`
  },

  exportArtists: (from: string, to: string): string => {
    return `${env.apiUrl}/artwork-ms/api/users/export-artists?min=${from}&max=${to}`
  },

  adminResetPassword: async (userId: number, newPassword: string): Promise<User> => {
    const response = await apiClient.post(`/api/users/${userId}/reset-password`, {
      password: newPassword,
    })
    return response.data
  },
}

